/** Common utility functions for Harmony controls */

/**
 * Counter for generating unique control IDs
 *
 * @internal
 */
let uniqueIdCounter = 0;

/**
 * Generates a unique ID for a control Useful for accessibility attributes (id, aria-labelledby, aria-describedby)
 *
 * @example
 *   ```typescript
 *   class HxCheckbox {
 *     readonly id = signal(generateUniqueId('hx-checkbox'));
 *   }
 *   ```;
 *
 * @param prefix - The prefix for the ID (e.g., 'hx-checkbox', 'hx-input')
 * @returns A unique ID string like 'hx-checkbox-0', 'hx-input-1', etc.
 */
export function generateUniqueId(prefix: string = 'hx-control'): string {
  return `${prefix}-${uniqueIdCounter++}`;
}

/**
 * Resets the unique ID counter Primarily for testing purposes
 *
 * @internal
 */
export function resetUniqueIdCounter(): void {
  uniqueIdCounter = 0;
}

/**
 * Coerces a value to a boolean Treats empty strings as true (for attribute presence)
 *
 * @example
 *   ```typescript
 *   coerceBoolean('') // true (attribute present with no value)
 *   coerceBoolean('false') // false
 *   coerceBoolean('true') // true
 *   coerceBoolean(true) // true
 *   ```;
 *
 * @param value - The value to coerce
 * @returns Boolean representation of the value
 */
export function coerceBoolean(value: any): boolean {
  return value != null && `${value}` !== 'false';
}

/**
 * Coerces a value to a number Returns NaN for invalid numbers
 *
 * @param value - The value to coerce
 * @returns Numeric representation of the value or NaN
 */
export function coerceNumber(value: any): number {
  return Number(value);
}

/**
 * Coerces a value to a number with a fallback
 *
 * @param value - The value to coerce
 * @param fallback - Fallback value if coercion fails (default: 0)
 * @returns Numeric representation or fallback
 */
export function coerceNumberOrDefault(value: any, fallback: number = 0): number {
  const num = coerceNumber(value);
  return isNaN(num) ? fallback : num;
}

/**
 * Clamps a number between a minimum and maximum value
 *
 * @param value - The value to clamp
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Checks if a value is defined (not null or undefined)
 *
 * @param value - Value to check
 * @returns True if value is defined
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Gets a CSS class string from a class object Filters out falsy values and joins remaining classes
 *
 * @example
 *   ```typescript
 *   getClassString({
 *     'base-class': true,
 *     'active': isActive,
 *     'disabled': false
 *   })
 *   // Returns: 'base-class active' (if isActive is true)
 *   ```;
 *
 * @param classes - Object mapping class names to boolean conditions
 * @returns Space-separated class string
 */
export function getClassString(classes: Record<string, boolean>): string {
  return Object.keys(classes)
    .filter((key) => classes[key])
    .join(' ');
}
